import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { showApiError, useMutation } from '@fv/client-core'
import type {
  UpdateMutedNotificationTypes,
  UpdateNotificationStatus,
  UserNotificationSettings,
} from '@fv/models'

import { shipperFetch } from '@/utils/shipperFetch'

import {
  useCountCacheModifier,
  useUpdateNotificationInCaches,
} from './cache-modifiers'
import { pinnedFilter } from './constants'
import { type Notification, notificationKeys } from './queries'

const updateNotificationStatus =
  (id?: string) =>
  async (dto: UpdateNotificationStatus): Promise<Notification | undefined> => {
    if (!id) return
    return await shipperFetch<Notification>(`/notifications/${id}`, {
      method: 'PUT',
      body: dto,
    })
  }
export const useUpdateNotificationStatus = (notification?: Notification) => {
  const unreadCountModifier = useCountCacheModifier({})
  const pinnedCountModifier = useCountCacheModifier(pinnedFilter)
  const cacheModifier = useUpdateNotificationInCaches()
  return useMutation({
    mutationFn: updateNotificationStatus(notification?._id),
    onSuccess: (resp, { read, pinned }) => {
      const readChanged =
        typeof read === 'boolean' && !!notification.readDate !== read
      const pinnedChanged = notification.pinned !== pinned
      if (!readChanged && !pinnedChanged) {
        return
      }
      readChanged && unreadCountModifier(read ? -1 : 1)
      pinnedChanged && pinnedCountModifier(pinned ? 1 : -1)
      cacheModifier({
        ...resp,
        pinned,
      })
    },
  })
}

const archiveNotification = async (id: string) => {
  return await shipperFetch(`/notifications/${id}`, {
    method: 'DELETE',
  })
}

export const useArchiveNotification = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: archiveNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
      queryClient.invalidateQueries({
        queryKey: notificationKeys.filteredCountBase(),
      })
      toast.success('Archived notification.', {
        id: 'notifications',
      })
    },
  })
}

export const useArchiveMultipleNotifications = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      return await Promise.all(ids.map(id => archiveNotification(id)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.filteredCountBase(),
      })
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

export const useMarkMultipleNotificationsAsRead = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      return await Promise.all(
        ids.map(id => updateNotificationStatus(id)({ read: true })),
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

const putMuteNotificationTypes = async (dto: UpdateMutedNotificationTypes) => {
  return await shipperFetch('/notifications/mute', {
    method: 'PUT',
    body: dto,
  })
}

export const useMuteNotificationTypes = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: putMuteNotificationTypes,
    onMutate(vars) {
      queryClient.setQueryData<UserNotificationSettings>(
        notificationKeys.userSettings,
        prev => ({
          ...prev,
          mutedNotificationTypes: vars.muted
            ? [...(prev.mutedNotificationTypes ?? []), ...vars.types]
            : prev.mutedNotificationTypes?.filter(
                type => !vars.types.includes(type),
              ),
        }),
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.counts })
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() })
    },
    onError: (e, vars) => {
      showApiError('Failed to update notification settings', e)
      queryClient.setQueryData<UserNotificationSettings>(
        // undo optimistic update
        notificationKeys.userSettings,
        prev => ({
          ...prev,
          mutedNotificationTypes: vars.muted
            ? prev.mutedNotificationTypes?.filter(
                type => !vars.types.includes(type),
              )
            : [...(prev.mutedNotificationTypes ?? []), ...vars.types],
        }),
      )
    },
  })
}
